import type { CaseFileUploadBatch } from "@/briefpoint-client";
import { ErrorReason } from "@/briefpoint-client";
import ExpandableText from "@/components/ExpandableText";
import { toast } from "@/components/ui/toast";
import styles from "./useBatchErrorToasts.module.scss";

const MAX_FILES_IN_LIST = 6;

function UnsupportedFromZipContent(props: { unsupportedNames: string[] }) {
  const listText = props.unsupportedNames.map((name) => `• ${name}`).join("\n");
  return (
    <div className={styles.batchToastContent}>
      <span className={styles.toastDescription}>
        Please convert the files below to PDF and reupload them.
      </span>
      {listText ? (
        <ExpandableText
          text={listText}
          lines={MAX_FILES_IN_LIST}
          fontSize={12}
          className={styles.fileListText}
        />
      ) : null}
    </div>
  );
}

function FileListToastContent(props: { message: string; filenames: string[] }) {
  const listText = props.filenames.map((name) => `• ${name}`).join("\n");
  return (
    <div className={styles.batchToastContent}>
      <span className={styles.toastDescription}>{props.message}</span>
      {listText ? (
        <ExpandableText
          text={listText}
          lines={MAX_FILES_IN_LIST}
          fontSize={12}
          className={styles.fileListText}
        />
      ) : null}
    </div>
  );
}

export default function useBatchErrorToasts() {
  return (batch: CaseFileUploadBatch) => {
    const errorResults = batch.errorResults ?? {};
    const errors = Object.entries(errorResults).reduce<{
      unsupportedFileTypes: string[];
      emptyFiles: string[];
      invalidSignatures: string[];
      fileHashMismatches: string[];
      duplicates: string[];
    }>(
      (acc, [filename, errorReason]: [string, ErrorReason]) => {
        if (errorReason === ErrorReason.UnsupportedFileType) {
          acc.unsupportedFileTypes.push(filename);
        } else if (errorReason === ErrorReason.EmptyFile) {
          acc.emptyFiles.push(filename);
        } else if (errorReason === ErrorReason.InvalidSignature) {
          acc.invalidSignatures.push(filename);
        } else if (errorReason === ErrorReason.FileHashMismatch) {
          acc.fileHashMismatches.push(filename);
        } else if (errorReason === ErrorReason.Duplicate) {
          acc.duplicates.push(filename);
        }
        return acc;
      },
      {
        unsupportedFileTypes: [],
        emptyFiles: [],
        invalidSignatures: [],
        fileHashMismatches: [],
        duplicates: [],
      },
    );

    // require user to dismiss; do not auto-dismiss
    const toastOptions = {
      closeButton: true as const,
      duration: Number.POSITIVE_INFINITY,
    };

    if (errors.duplicates.length > 0) {
      toast.warning(
        <FileListToastContent
          message="Some files were not uploaded because duplicates were found."
          filenames={errors.duplicates}
        />,
        toastOptions,
      );
    }

    if (errors.unsupportedFileTypes.length > 0) {
      toast.warning(
        <UnsupportedFromZipContent
          unsupportedNames={errors.unsupportedFileTypes}
        />,
        toastOptions,
      );
    }

    if (errors.emptyFiles.length > 0) {
      toast.warning(
        <FileListToastContent
          message="Some files were not uploaded because they are empty."
          filenames={errors.emptyFiles}
        />,
        toastOptions,
      );
    }

    if (errors.invalidSignatures.length > 0) {
      toast.warning(
        <FileListToastContent
          message="Some files were not uploaded because they have invalid signatures."
          filenames={errors.invalidSignatures}
        />,
        toastOptions,
      );
    }

    if (errors.fileHashMismatches.length > 0) {
      toast.warning(
        <FileListToastContent
          message="Some files were not uploaded because they have mismatched file hashes."
          filenames={errors.fileHashMismatches}
        />,
        toastOptions,
      );
    }
  };
}
